import { useCallback, useEffect } from 'react';
import useImpressionTracking from 'src/hooks/useImpressionTracking';
import usePolarisActor from 'src/hooks/usePolarisActor';
import getIntroMessage from 'src/interviews/utils/getIntroMessage';
import { trackInterviewViewed } from '../analytics';
import useChatList from '../hooks/useChatList';
import useInterview from '../hooks/useInterview';
import useInterviewType from '../hooks/useInterviewType';
import usePeekDifferential from '../hooks/usePeekDifferential';
import useResults from '../hooks/useResults';
import useTransition from '../hooks/useTransition';
import DXOCAlarmMessage from './Completion/DiagnosisOfConcern/components/DXOCAlarmMessage';
import Completion from './Completion/index';
import useIsDoctorConfirmationEnabled from './DoctorConfirmation/useIsDoctorConfirmationEnabled';
import Layout from './Layout';
import MessageBox from './MessageBox';
import PeekDifferential from './PeekDifferential';
import Question from './Question';

import type { IDynamicInterviewProps, IQuestionWithIndex } from 'src/packages/polaris/types';

export const InterviewContainer = ({
  conditionName,
  formLocation,
  isEmbedded,
  slug,
  onQuestionDisplay,
  onQuestionSubmit,
  polarisProps,
}: Pick<
  IDynamicInterviewProps,
  | 'conditionName'
  | 'formLocation'
  | 'isEmbedded'
  | 'slug'
  | 'polarisProps'
  | 'onQuestionDisplay'
  | 'onQuestionSubmit'
>) => {
  const transition = useTransition();
  const chatList = useChatList();
  const result = useResults();
  const interview = useInterview();
  const peekDifferential = usePeekDifferential();
  const interviewType = useInterviewType();
  const state = usePolarisActor()?.getSnapshot();
  const transitionIntroMessage = state?.context.introText;
  const activeQuestion: IQuestionWithIndex | undefined = chatList.items[chatList.active];

  useEffect(() => {
    // New question or result loaded
    onQuestionDisplay?.(chatList.active);
  }, [chatList.active, result]);

  const trackViewed = useCallback(() => {
    if (interview.token) {
      trackInterviewViewed(interview.token || '', interviewType, formLocation);
    }
  }, [interview.token, interviewType, formLocation]);

  const handleExpanded = useCallback(() => {
    trackViewed();
  }, [trackViewed]);

  const impressionRef = useImpressionTracking(() => {
    if (polarisProps?.displayPolarisUI || polarisProps?.defaultExpanded) {
      trackViewed();
    }
  });

  const handleRestart = useCallback(() => {
    transition({
      event: 'RESET',
    });
  }, [transition]);

  const { chargeAmount, isEnabled: isDxConfirmationEnabled } =
    useIsDoctorConfirmationEnabled(interviewType);

  const handleGoBack = useCallback(() => {
    transition({
      event: 'BACK',
    });
  }, [transition]);

  const { showProgressBar = true } = polarisProps || {};

  return (
    <div ref={impressionRef}>
      <Layout
        defaultExpanded={polarisProps?.defaultExpanded}
        handleExit={polarisProps?.onExit}
        isAccordionEmbeddedLayout={!!polarisProps}
        isEmbedded={isEmbedded}
        onExpanded={handleExpanded}
        showRestartButton={!!result || chatList.active !== 0}
        showProgressBar={showProgressBar && !result}
        triggerRestart={handleRestart}
        title={polarisProps?.titleQuestion}
        handleGoBack={handleGoBack}
      >
        {result ? (
          <Completion
            conditionName={conditionName || ''}
            polarisProps={polarisProps}
            result={result}
            slug={slug}
          />
        ) : (
          <>
            {!!peekDifferential && <PeekDifferential item={peekDifferential} />}
            {activeQuestion?.dxoc_is_alarming && (
              <DXOCAlarmMessage isInitialWarning conditionName={conditionName} />
            )}
            {!activeQuestion?.dxoc_is_alarming &&
              isDxConfirmationEnabled &&
              activeQuestion?.index === 0 && (
                <MessageBox
                  alarmMessage={
                    <>
                      <span className="bp-font-bold">New!</span> After you finish your free
                      assessment, you’ll have the option to pay ${chargeAmount} for a licensed
                      physician to review and confirm your results.
                    </>
                  }
                  color="gray"
                  variant="new"
                />
              )}
            <Question
              introText={
                chatList.active === 0
                  ? transitionIntroMessage ||
                    getIntroMessage({ interviewType, conditionName: conditionName || '' })
                  : activeQuestion?.introText
              }
              onQuestionSubmit={onQuestionSubmit}
              shouldShowSubmitButton
              question={activeQuestion}
              slug={slug}
            />
          </>
        )}
      </Layout>
    </div>
  );
};

export default InterviewContainer;
